// src/App.js
import React, { useState } from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import './App.css';
import { initialData } from './data/initialData';

// ===== SECTION TEMPLATES =====
const SECTION_TEMPLATES = [
  {
    id: 'template-blank',
    name: '📄 Blank Section',
    title: 'New Section',
    questions: [
      {
        id: 'temp-q-1',
        type: 'text',
        label: 'New Question',
        name: 'new_question',
        placeholder: 'Enter answer',
        value: ''
      }
    ]
  },
  {
    id: 'template-text-only',
    name: '📝 Text Only',
    title: 'Text Section',
    questions: [
      {
        id: 'temp-q-1',
        type: 'text',
        label: 'Question 1',
        name: 'text_q1',
        placeholder: 'Enter answer',
        value: ''
      },
      {
        id: 'temp-q-2',
        type: 'text',
        label: 'Question 2',
        name: 'text_q2',
        placeholder: 'Enter answer',
        value: ''
      }
    ]
  },
  {
    id: 'template-dropdown',
    name: '📋 Dropdown Section',
    title: 'Dropdown Section',
    questions: [
      {
        id: 'temp-q-1',
        type: 'dropdown',
        label: 'Select Option',
        name: 'dropdown_q1',
        options: ['Select', 'Option 1', 'Option 2', 'Option 3'],
        value: ''
      },
      {
        id: 'temp-q-2',
        type: 'dropdown',
        label: 'Another Dropdown',
        name: 'dropdown_q2',
        options: ['Select', 'Yes', 'No'],
        value: ''
      }
    ]
  },
  {
    id: 'template-checkbox',
    name: '☑️ Checkbox Section',
    title: 'Checkbox Section',
    questions: [
      {
        id: 'temp-q-1',
        type: 'checkbox',
        label: 'Select Options',
        name: 'checkbox_q1',
        options: ['Option 1', 'Option 2', 'Option 3'],
        value: []
      }
    ]
  },
  {
    id: 'template-carrier',
    name: '🏢 Carrier Section',
    title: 'Carrier Info',
    questions: [
      {
        id: 'temp-q-1',
        type: 'subgrid',
        label: 'Carrier Details',
        name: 'carrier_details',
        fields: [
          { id: 'temp-f-1', type: 'text', label: 'Name', name: 'carrier_name', placeholder: 'Enter name', value: '' },
          { id: 'temp-f-2', type: 'email', label: 'Email', name: 'carrier_email', placeholder: 'Enter email', value: '' },
          { id: 'temp-f-3', type: 'text', label: 'Phone', name: 'carrier_phone', placeholder: 'Enter phone', value: '' }
        ]
      }
    ]
  },
  {
    id: 'template-life',
    name: '🛡️ Basic Life Section',
    title: 'Basic Life',
    questions: [
      {
        id: 'temp-q-1',
        type: 'text',
        label: 'Group Number',
        name: 'group_number',
        placeholder: 'Enter group number',
        value: ''
      },
      {
        id: 'temp-q-2',
        type: 'text',
        label: 'Benefit Amount',
        name: 'benefit_amount',
        placeholder: 'Enter amount',
        value: ''
      },
      {
        id: 'temp-q-3',
        type: 'dropdown',
        label: 'Coverage Rounding',
        name: 'coverage_rounding',
        options: ['Select', 'No Rounding', 'Next Highest $25,000', 'Nearest $25,000', 'Round down to $25,000'],
        value: ''
      }
    ]
  },
  {
    id: 'template-medical',
    name: '🏥 Medical Section',
    title: 'Medical',
    questions: [
      {
        id: 'temp-q-1',
        type: 'text',
        label: 'Group Number',
        name: 'medical_group',
        placeholder: 'Enter group number',
        value: ''
      },
      {
        id: 'temp-q-2',
        type: 'dropdown',
        label: 'HSA Compatible?',
        name: 'hsa_compatible',
        options: ['Select', 'Yes', 'No'],
        value: ''
      },
      {
        id: 'temp-q-3',
        type: 'dropdown',
        label: 'Waive Coverage Requirement',
        name: 'waive_requirement',
        options: ['Select', 'Document', 'Reason', 'Document and Reason', 'Nothing'],
        value: ''
      }
    ]
  }
];

// ===== CARRIER SUBGRID DEFAULT FIELDS =====
// Matches the Name / Email / Phone Number format used by every existing
// question in the Carriers section (dd_impl, dd_am, eq_impl, eq_am,
// sfh_impl, sfh_am in initialData.js).
const getCarrierDefaultFields = () => {
  const ts = Date.now();
  return [
    { id: `f-${ts}-1`, type: 'text', label: 'Name', name: `carrier_name_${ts}`, placeholder: 'Enter name', value: '' },
    { id: `f-${ts}-2`, type: 'email', label: 'Email', name: `carrier_email_${ts}`, placeholder: 'Enter email', value: '' },
    { id: `f-${ts}-3`, type: 'text', label: 'Phone Number', name: `carrier_phone_${ts}`, placeholder: 'Enter phone', value: '' }
  ];
};

// A section counts as a "Carrier" section if its title mentions "carrier".
// This covers the built-in "Carriers" section as well as any section added
// from the "🏢 Carrier Section" template (titled "Carrier Info").
const isCarrierSectionTitle = (title) => !!title && title.toLowerCase().includes('carrier');

function App() {
  const [formData, setFormData] = useState(initialData);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showOptionEditor, setShowOptionEditor] = useState(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [showAddQuestionDialog, setShowAddQuestionDialog] = useState(false);
  const [addQuestionSectionId, setAddQuestionSectionId] = useState(null);
  const [addQuestionPosition, setAddQuestionPosition] = useState(null);
  const [newQuestionData, setNewQuestionData] = useState({
    type: 'text',
    label: '',
    placeholder: '',
    options: ['Option 1', 'Option 2', 'Option 3'],
    fields: []
  });
  const [copySourceId, setCopySourceId] = useState('');
  const [showCopyQuestionDialog, setShowCopyQuestionDialog] = useState(false);
  const [copyQuestionTargetSectionId, setCopyQuestionTargetSectionId] = useState(null);
  const [copyQuestionPosition, setCopyQuestionPosition] = useState(null);
  const [copyQuestionSourceId, setCopyQuestionSourceId] = useState('');
  const [copyQuestionSectionFilter, setCopyQuestionSectionFilter] = useState('');
  const [copyQuestionSearchTerm, setCopyQuestionSearchTerm] = useState('');

  // ===== UPDATE FIELD =====
  const updateField = (sectionId, questionId, value) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            return { ...q, value: value };
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  // ===== UPDATE DROPDOWN OPTIONS IN TEMPLATE =====
  const updateTemplateOption = (questionIndex, optionIndex, newValue) => {
    if (!selectedTemplate) return;
    const updatedTemplate = { ...selectedTemplate };
    const question = updatedTemplate.questions[questionIndex];
    if (question.type === 'dropdown') {
      const newOptions = [...question.options];
      newOptions[optionIndex] = newValue;
      question.options = newOptions;
    }
    setSelectedTemplate(updatedTemplate);
  };

  const addTemplateOption = (questionIndex) => {
    if (!selectedTemplate) return;
    const newOption = prompt('Enter new option name:');
    if (newOption && newOption.trim()) {
      const updatedTemplate = { ...selectedTemplate };
      const question = updatedTemplate.questions[questionIndex];
      if (question.type === 'dropdown') {
        question.options = [...question.options, newOption.trim()];
      }
      setSelectedTemplate(updatedTemplate);
    }
  };

  const deleteTemplateOption = (questionIndex, optionIndex) => {
    if (!selectedTemplate) return;
    if (window.confirm('Are you sure you want to delete this option?')) {
      const updatedTemplate = { ...selectedTemplate };
      const question = updatedTemplate.questions[questionIndex];
      if (question.type === 'dropdown') {
        question.options = question.options.filter((_, i) => i !== optionIndex);
      }
      setSelectedTemplate(updatedTemplate);
    }
  };

  const selectTemplateForEditing = (template) => {
    setSelectedTemplate(JSON.parse(JSON.stringify(template)));
    setShowOptionEditor(true);
    setSelectedSectionIndex(null);
  };

  // ===== COPY EXISTING PLAN SECTION =====
  // Lets the user duplicate an existing "Plans — ..." section (its full
  // structure AND whatever answers are already filled in) instead of
  // building a new one from scratch when two plans share the same fields -
  // e.g. copying "Plans — Medical" into a new "Plans — Dental" section.
  // Reuses the exact same selectedTemplate + addSectionFromTemplate flow
  // already used for section templates, so title editing, insert position,
  // and per-question editing all work identically to the template path.
  const copyExistingSection = () => {
    if (!copySourceId) return;
    const source = formData.sections.find(s => s.id === copySourceId);
    if (!source) return;
    const cloned = JSON.parse(JSON.stringify(source));
    setSelectedTemplate(cloned);
    setShowOptionEditor(true);
    setSelectedSectionIndex(null);
    setCopySourceId('');
  };

  // ===== COPY EXISTING QUESTION =====
  // Lets the user duplicate any single question - from any section, whether
  // it's one of the original sections or one just created - into another
  // section, instead of rebuilding it from scratch (e.g. copying "Group
  // Number" from Plans — Medical straight into Plans — Dental). Unlike the
  // "Add Question" dialog, this clones the FULL question object exactly as
  // it is (options, groups, fields, radioWithBlanks segments/blankValues,
  // current value, everything), so nothing is lost even for the more
  // complex question types. The source list is built fresh from formData on
  // every render, so any section - old or newly added - is always available
  // both as a source to copy from and, via the button placed on every
  // section, as a destination to copy into.
  const openCopyQuestionDialog = (sectionId) => {
    setCopyQuestionTargetSectionId(sectionId);
    setCopyQuestionSourceId('');
    setCopyQuestionSectionFilter('');
    setCopyQuestionSearchTerm('');
    setCopyQuestionPosition(null);
    setShowCopyQuestionDialog(true);
  };

  const copyQuestionIntoSection = () => {
    if (!copyQuestionSourceId || !copyQuestionTargetSectionId) return;

    let sourceQuestion = null;
    for (const section of formData.sections) {
      const found = section.questions.find(q => q.id === copyQuestionSourceId);
      if (found) {
        sourceQuestion = found;
        break;
      }
    }
    if (!sourceQuestion) return;

    const cloned = {
      ...JSON.parse(JSON.stringify(sourceQuestion)),
      id: `q-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      name: sourceQuestion.name + '_' + Date.now()
    };

    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id !== copyQuestionTargetSectionId) return section;
        const newQuestions = [...section.questions];
        if (copyQuestionPosition !== null && copyQuestionPosition < newQuestions.length) {
          newQuestions.splice(copyQuestionPosition + 1, 0, cloned);
        } else {
          newQuestions.push(cloned);
        }
        return { ...section, questions: newQuestions };
      })
    }));

    setShowCopyQuestionDialog(false);
    setCopyQuestionTargetSectionId(null);
    setCopyQuestionSourceId('');
    setCopyQuestionSectionFilter('');
    setCopyQuestionSearchTerm('');
    setCopyQuestionPosition(null);
  };

  const addSectionFromTemplate = () => {
    if (!selectedTemplate) return;
    
    const newSection = {
      id: `section-${Date.now()}`,
      title: selectedTemplate.title + ' ' + (formData.sections.filter(s => s.title.includes(selectedTemplate.title)).length + 1),
      questions: selectedTemplate.questions.map(q => ({
        ...q,
        id: `q-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
        name: q.name + '_' + Date.now()
      }))
    };
    
    setFormData(prev => {
      const newSections = [...prev.sections];
      if (selectedSectionIndex !== null && selectedSectionIndex < newSections.length) {
        newSections.splice(selectedSectionIndex + 1, 0, newSection);
      } else {
        newSections.push(newSection);
      }
      return { ...prev, sections: newSections };
    });
    
    setShowOptionEditor(false);
    setSelectedTemplate(null);
    setShowTemplateDialog(false);
    setSelectedSectionIndex(null);
  };

  const deleteSection = (sectionId) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      setFormData(prev => ({
        ...prev,
        sections: prev.sections.filter(s => s.id !== sectionId)
      }));
    }
  };

  // ===== OPEN ADD QUESTION DIALOG =====
  // If the question is being added to a "Carriers"-titled section, default
  // the new question to a Subgrid pre-populated with Name / Email / Phone
  // Number fields, matching every other question already in that section
  // (Initial Implementation, Account Manager, etc). Every other section
  // keeps the original plain-text default.
  const openAddQuestionDialog = (sectionId) => {
    setAddQuestionSectionId(sectionId);
    setAddQuestionPosition(null);

    const section = formData.sections.find(s => s.id === sectionId);
    const isCarrierSection = isCarrierSectionTitle(section && section.title);

    if (isCarrierSection) {
      setNewQuestionData({
        type: 'subgrid',
        label: '',
        placeholder: 'Enter answer',
        options: ['Option 1', 'Option 2', 'Option 3'],
        fields: getCarrierDefaultFields()
      });
    } else {
      setNewQuestionData({
        type: 'text',
        label: '',
        placeholder: 'Enter answer',
        options: ['Option 1', 'Option 2', 'Option 3'],
        fields: []
      });
    }

    setShowAddQuestionDialog(true);
  };

  const addQuestionToSection = () => {
    if (!newQuestionData.label.trim()) {
      alert('Please enter a question label');
      return;
    }

    let question = {
      id: `q-${Date.now()}`,
      type: newQuestionData.type,
      label: newQuestionData.label,
      name: `q_${Date.now()}`,
      value: newQuestionData.type === 'checkbox' ? [] : newQuestionData.type === 'checkboxgroup' ? {} : ''
    };

    if (newQuestionData.type === 'text' || newQuestionData.type === 'number' || newQuestionData.type === 'email') {
      question.placeholder = newQuestionData.placeholder || 'Enter answer';
    }

    if (newQuestionData.type === 'dropdown') {
      question.options = newQuestionData.options.filter(opt => opt.trim() !== '');
      question.value = '';
    }

    if (newQuestionData.type === 'checkbox') {
      question.options = newQuestionData.options.filter(opt => opt.trim() !== '');
      question.value = [];
    }

    if (newQuestionData.type === 'checkboxgroup') {
      question.groups = newQuestionData.fields;
      question.value = {};
    }

    if (newQuestionData.type === 'subgrid') {
      question.fields = newQuestionData.fields;
    }

    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== addQuestionSectionId) return section;
        const newQuestions = [...section.questions];
        if (addQuestionPosition !== null && addQuestionPosition < newQuestions.length) {
          newQuestions.splice(addQuestionPosition + 1, 0, question);
        } else {
          newQuestions.push(question);
        }
        return {
          ...section,
          questions: newQuestions
        };
      });
      return { ...prev, sections: newSections };
    });

    setShowAddQuestionDialog(false);
    setAddQuestionSectionId(null);
    setAddQuestionPosition(null);
    setNewQuestionData({
      type: 'text',
      label: '',
      placeholder: 'Enter answer',
      options: ['Option 1', 'Option 2', 'Option 3'],
      fields: []
    });
  };

  const updateNewQuestionOption = (index, value) => {
    const newOptions = [...newQuestionData.options];
    newOptions[index] = value;
    setNewQuestionData({ ...newQuestionData, options: newOptions });
  };

  const addNewQuestionOption = () => {
    setNewQuestionData({
      ...newQuestionData,
      options: [...newQuestionData.options, `Option ${newQuestionData.options.length + 1}`]
    });
  };

  const deleteNewQuestionOption = (index) => {
    if (window.confirm('Delete this option?')) {
      const newOptions = newQuestionData.options.filter((_, i) => i !== index);
      setNewQuestionData({ ...newQuestionData, options: newOptions });
    }
  };

  const updateSubgridField = (index, field, value) => {
    const newFields = [...newQuestionData.fields];
    newFields[index][field] = value;
    setNewQuestionData({ ...newQuestionData, fields: newFields });
  };

  const addSubgridField = () => {
    setNewQuestionData({
      ...newQuestionData,
      fields: [
        ...newQuestionData.fields,
        { id: `f-${Date.now()}`, type: 'text', label: '', name: `field_${Date.now()}`, placeholder: '', value: '' }
      ]
    });
  };

  const deleteSubgridField = (index) => {
    if (window.confirm('Delete this field?')) {
      const newFields = newQuestionData.fields.filter((_, i) => i !== index);
      setNewQuestionData({ ...newQuestionData, fields: newFields });
    }
  };

  const updateCheckboxGroupField = (index, field, value) => {
    const newFields = [...newQuestionData.fields];
    newFields[index][field] = value;
    setNewQuestionData({ ...newQuestionData, fields: newFields });
  };

  const addCheckboxGroupField = () => {
    setNewQuestionData({
      ...newQuestionData,
      fields: [
        ...newQuestionData.fields,
        { label: 'Group Label', name: `group_${Date.now()}`, options: ['Option 1', 'Option 2'] }
      ]
    });
  };

  const updateCheckboxGroupOption = (fieldIndex, optionIndex, value) => {
    const newFields = [...newQuestionData.fields];
    newFields[fieldIndex].options[optionIndex] = value;
    setNewQuestionData({ ...newQuestionData, fields: newFields });
  };

  const addCheckboxGroupOption = (fieldIndex) => {
    const newFields = [...newQuestionData.fields];
    newFields[fieldIndex].options.push(`Option ${newFields[fieldIndex].options.length + 1}`);
    setNewQuestionData({ ...newQuestionData, fields: newFields });
  };

  const deleteCheckboxGroupOption = (fieldIndex, optionIndex) => {
    const newFields = [...newQuestionData.fields];
    newFields[fieldIndex].options = newFields[fieldIndex].options.filter((_, i) => i !== optionIndex);
    setNewQuestionData({ ...newQuestionData, fields: newFields });
  };

  const deleteQuestion = (sectionId, questionId) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      setFormData(prev => {
        const newSections = prev.sections.map(section => {
          if (section.id !== sectionId) return section;
          return {
            ...section,
            questions: section.questions.filter(q => q.id !== questionId)
          };
        });
        return { ...prev, sections: newSections };
      });
    }
  };

  const updateSectionTitle = (sectionId, newTitle) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return { ...section, title: newTitle };
      });
      return { ...prev, sections: newSections };
    });
  };

  const updateQuestionLabel = (sectionId, questionId, newLabel) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            return { ...q, label: newLabel };
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  const updateCheckbox = (sectionId, questionId, optionValue, checked) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            const currentValues = q.value || [];
            if (checked) {
              return { ...q, value: [...currentValues, optionValue] };
            } else {
              return { ...q, value: currentValues.filter(v => v !== optionValue) };
            }
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  const updateCheckboxGroup = (sectionId, questionId, groupName, optionValue, checked) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            const currentGroupValues = (q.value && q.value[groupName]) || [];
            let updatedGroupValues;
            if (checked) {
              updatedGroupValues = [...currentGroupValues, optionValue];
            } else {
              updatedGroupValues = currentGroupValues.filter(v => v !== optionValue);
            }
            return {
              ...q,
              value: {
                ...(q.value || {}),
                [groupName]: updatedGroupValues
              }
            };
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  // ===== YES/NO WITH TABLE (e.g. Employee Age Reduction schedule) =====
  // When the Yes/No answer is "Yes", shows an editable table of rows (e.g.
  // "Benefit reduces %" / "At Age") that the user builds up with Add New /
  // Delete, since the number of rows and their values vary per client -
  // nothing is pre-filled. Rows are always kept in question.rows even when
  // the answer is "No", so toggling back to "Yes" doesn't lose data already
  // entered.
  const updateYesNoTableValue = (sectionId, questionId, value) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            return { ...q, value: value };
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  const addYesNoTableRow = (sectionId, questionId) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            const newRow = { id: `row-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`, percent: '', age: '' };
            return { ...q, rows: [...(q.rows || []), newRow] };
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  const updateYesNoTableRow = (sectionId, questionId, rowId, field, value) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            return {
              ...q,
              rows: (q.rows || []).map(row => row.id === rowId ? { ...row, [field]: value } : row)
            };
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  const deleteYesNoTableRow = (sectionId, questionId, rowId) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            return { ...q, rows: (q.rows || []).filter(row => row.id !== rowId) };
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  // ===== RADIO WITH BLANKS (e.g. Voluntary Life GI Options during OE) =====
  // Sets which single option (option1/option2/option3) is selected. Only one
  // option can be true at a time, so this is a radio group, not checkboxes.
  const updateRadioSelection = (sectionId, questionId, optionId) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            return { ...q, value: optionId };
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  // Updates one of the inline numeric blanks embedded in an option's sentence
  // (e.g. the "enrolled" / "notEnrolled" increment counts under Option 1).
  const updateRadioBlankValue = (sectionId, questionId, blankId, val) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            return { ...q, blankValues: { ...(q.blankValues || {}), [blankId]: val } };
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  // ===== CHECKBOX WITH BLANKS (e.g. Employee Guaranteed Issue Amount, =====
  // ===== STD Benefit Amount / Premiums / Premiums Sharing) ==============
  // Unlike radioWithBlanks, more than one of these option sentences can
  // apply at once (e.g. multiple GI Amount definitions, or several Premiums
  // Sharing rules stacked together), so this toggles membership of optionId
  // inside question.value (an array) - the same pattern updateCheckbox uses
  // for a plain checkbox question - instead of a single selected id.
  const updateCheckboxWithBlanksToggle = (sectionId, questionId, optionId, checked) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            const currentValues = q.value || [];
            const newValues = checked
              ? (currentValues.includes(optionId) ? currentValues : [...currentValues, optionId])
              : currentValues.filter(v => v !== optionId);
            return { ...q, value: newValues };
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  // Updates one of the inline blanks embedded in an option's sentence. Also
  // doubles as the setter for inline "dropdown" segments (e.g. the
  // Salary/Benefit picker inside the STD "Premiums?" option), since both are
  // just keyed values stored under blankValues[blankId].
  const updateCheckboxBlankValue = (sectionId, questionId, blankId, val) => {
    setFormData(prev => {
      const newSections = prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id !== questionId) return q;
            return { ...q, blankValues: { ...(q.blankValues || {}), [blankId]: val } };
          })
        };
      });
      return { ...prev, sections: newSections };
    });
  };

  // ===== GENERATE FILLABLE PDF =====
  const generatePDF = async () => {
    try {
      const btn = document.querySelector('.toolbar-btn.pdf');
      const originalText = btn.innerHTML;
      btn.innerHTML = '⏳ Generating PDF...';
      btn.disabled = true;

      const pdfDoc = await PDFDocument.create();
      let currentPage = pdfDoc.addPage([612, 792]);
      const { width, height } = currentPage.getSize();
      
      const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      
      const margin = 50;
      const contentWidth = width - (margin * 2);
      
      // Layout Metrics Setup
      let currentY = height - 60;
      let fieldCount = 0;
      
      // Document Title Header
      currentPage.drawText('Client Implementation Clarifications', {
        x: margin,
        y: currentY,
        size: 18,
        font: helveticaBold,
        color: rgb(44/255, 95/255, 138/255),
      });
      currentY -= 18;
      
      currentPage.drawText('Please fill in all fields below:', {
        x: margin,
        y: currentY,
        size: 10,
        font: helvetica,
        color: rgb(0.4, 0.4, 0.4),
      });
      currentY -= 35;
      
      const form = pdfDoc.getForm();
      const sections = formData.sections;

      // NOTE: checkPageOverflow now returns whether it started a new page,
      // and every place that computes a y-coordinate does so AFTER calling
      // this, never before. That ordering was the root cause of fields
      // landing on top of each other after a page break (positions were
      // calculated against the old page's currentY, then used on the new
      // page after currentY had been reset).
      const checkPageOverflow = (neededHeight) => {
        if (currentY - neededHeight < 60) {
          currentPage = pdfDoc.addPage([612, 792]);
          currentY = height - 60;
          return true;
        }
        return false;
      };

      // Word-wraps a question label to fit within maxWidth and draws it,
      // one line at a time, at x/currentY. Previously every question label
      // was drawn as a single drawText() call with no wrapping at all - fine
      // for short labels, but a long one (e.g. "For the coverage with salary
      // increments, how do the benefit increase due to salary increase be
      // handled?") could run straight past the page's right edge instead of
      // moving to the next line. Each extra line gets its own page-overflow
      // check (like renderOptionWithBlanks already does for radio options),
      // so a very long label pushes onto a new PDF page cleanly rather than
      // overlapping whatever comes after it. currentY is left sitting at the
      // last line drawn, ready for the caller's normal trailing spacing.
      const LABEL_LINE_HEIGHT = 12;
      const drawWrappedLabel = (text, x, maxWidth, size, font, color) => {
        const words = (text || '').split(' ');
        const lines = [];
        let current = '';
        words.forEach((word) => {
          const test = current ? current + ' ' + word : word;
          if (font.widthOfTextAtSize(test, size) > maxWidth && current) {
            lines.push(current);
            current = word;
          } else {
            current = test;
          }
        });
        if (current) lines.push(current);

        lines.forEach((line, li) => {
          if (li > 0) {
            checkPageOverflow(LABEL_LINE_HEIGHT + 10);
            currentY -= LABEL_LINE_HEIGHT;
          }
          currentPage.drawText(line, {
            x: x,
            y: currentY - 10,
            size: size,
            font: font,
            color: color,
          });
        });

        return lines.length;
      };

      // Renders one radioWithBlanks option: word-wraps its segments, and for
      // each "blank" segment places a REAL interactive text field at that
      // exact position in the wrapped line (rather than baking a static
      // placeholder like "____" into the text) so the blank is actually
      // fillable once the PDF is downloaded.
      // Widened from 42/12 - fields were too cramped for real entries like
      // "$25,000" or a salary multiple (Employee Guaranteed Issue Amount,
      // STD Benefit Amount, etc. all use this).
      const BLANK_FIELD_WIDTH = 68;
      const BLANK_FIELD_HEIGHT = 14;
      // Taller blanks need more vertical room between wrapped lines too, or
      // boxes on adjacent lines start to overlap.
      const BLANK_LINE_HEIGHT = 15;
      const renderOptionWithBlanks = (sIdx, qIdx, questionObj, opt, radioGroup, indentX) => {
        const tokens = [];
        (opt.segments || []).forEach((seg) => {
          if (seg.type === 'text') {
            seg.text.split(' ').filter(Boolean).forEach((w) => {
              tokens.push({ type: 'word', text: w });
            });
          } else if (seg.type === 'blank') {
            tokens.push({ type: 'blank', id: seg.id, placeholder: seg.placeholder || '#' });
          }
        });

        const spaceWidth = helvetica.widthOfTextAtSize(' ', 8);
        const maxLineWidth = contentWidth - indentX;

        const lines = [];
        let currentLine = [];
        let currentLineWidth = 0;
        tokens.forEach((tok) => {
          const tokWidth = tok.type === 'word'
            ? helvetica.widthOfTextAtSize(tok.text, 8)
            : BLANK_FIELD_WIDTH;
          const neededWidth = currentLine.length === 0 ? tokWidth : currentLineWidth + spaceWidth + tokWidth;
          if (neededWidth > maxLineWidth && currentLine.length > 0) {
            lines.push(currentLine);
            currentLine = [tok];
            currentLineWidth = tokWidth;
          } else {
            currentLine.push(tok);
            currentLineWidth = neededWidth;
          }
        });
        if (currentLine.length > 0) lines.push(currentLine);

        lines.forEach((line, lineIdx) => {
          checkPageOverflow(16);

          if (lineIdx === 0) {
            radioGroup.addOptionToPage(opt.id, currentPage, {
              x: margin + 4,
              y: currentY - 9,
              width: 9,
              height: 9,
            });
          }

          let xPos = margin + indentX;
          line.forEach((tok) => {
            if (tok.type === 'word') {
              currentPage.drawText(tok.text, {
                x: xPos,
                y: currentY - 9,
                size: 8,
                font: helvetica,
                color: rgb(0.2, 0.2, 0.2),
              });
              xPos += helvetica.widthOfTextAtSize(tok.text, 8) + spaceWidth;
            } else if (tok.type === 'blank') {
              currentPage.drawRectangle({
                x: xPos,
                y: currentY - 11,
                width: BLANK_FIELD_WIDTH,
                height: BLANK_FIELD_HEIGHT,
                color: rgb(0.98, 0.98, 0.98),
                borderColor: rgb(0.6, 0.6, 0.6),
                borderWidth: 0.5,
              });

              const fName = `blank_${sIdx}_${qIdx}_${opt.id}_${tok.id}_${fieldCount++}`;
              const blankField = form.createTextField(fName);
              blankField.setText((questionObj.blankValues && questionObj.blankValues[tok.id]) || '');
              blankField.addToPage(currentPage, {
                x: xPos + 1,
                y: currentY - 10,
                width: BLANK_FIELD_WIDTH - 2,
                height: BLANK_FIELD_HEIGHT - 2,
              });
              blankField.setFontSize(8);

              xPos += BLANK_FIELD_WIDTH + spaceWidth;
            }
          });

          currentY -= BLANK_LINE_HEIGHT;
        });

        currentY -= 8;
      };

      // Renders one checkboxWithBlanks option: same word-wrap + real-field
      // approach as renderOptionWithBlanks above, but places an independent
      // checkbox (not part of a shared radio group, since several options
      // can be true at once) and also supports an inline "dropdown" segment
      // (used by the STD "Premiums?" option: "...% of Weekly [Salary/Benefit]").
      const renderCheckboxOptionWithBlanks = (sIdx, qIdx, questionObj, opt, indentX) => {
        const tokens = [];
        (opt.segments || []).forEach((seg) => {
          if (seg.type === 'text') {
            seg.text.split(' ').filter(Boolean).forEach((w) => {
              tokens.push({ type: 'word', text: w });
            });
          } else if (seg.type === 'blank') {
            tokens.push({ type: 'blank', id: seg.id, placeholder: seg.placeholder || '#' });
          } else if (seg.type === 'dropdown') {
            tokens.push({ type: 'dropdown', id: seg.id, options: seg.options || ['Select'] });
          }
        });

        const DROPDOWN_TOKEN_WIDTH = 100;
        const spaceWidth = helvetica.widthOfTextAtSize(' ', 8);
        const maxLineWidth = contentWidth - indentX;

        const lines = [];
        let currentLine = [];
        let currentLineWidth = 0;
        tokens.forEach((tok) => {
          const tokWidth = tok.type === 'word'
            ? helvetica.widthOfTextAtSize(tok.text, 8)
            : (tok.type === 'dropdown' ? DROPDOWN_TOKEN_WIDTH : BLANK_FIELD_WIDTH);
          const neededWidth = currentLine.length === 0 ? tokWidth : currentLineWidth + spaceWidth + tokWidth;
          if (neededWidth > maxLineWidth && currentLine.length > 0) {
            lines.push(currentLine);
            currentLine = [tok];
            currentLineWidth = tokWidth;
          } else {
            currentLine.push(tok);
            currentLineWidth = neededWidth;
          }
        });
        if (currentLine.length > 0) lines.push(currentLine);

        lines.forEach((line, lineIdx) => {
          checkPageOverflow(16);

          if (lineIdx === 0) {
            const cbField = form.createCheckBox(`cb_${sIdx}_${qIdx}_${opt.id}_${fieldCount++}`);
            if ((questionObj.value || []).includes(opt.id)) cbField.check();
            cbField.addToPage(currentPage, {
              x: margin + 4,
              y: currentY - 10,
              width: 9,
              height: 9,
              borderColor: rgb(0.4, 0.4, 0.4),
              borderWidth: 0.5,
            });
          }

          let xPos = margin + indentX;
          line.forEach((tok) => {
            if (tok.type === 'word') {
              currentPage.drawText(tok.text, {
                x: xPos,
                y: currentY - 9,
                size: 8,
                font: helvetica,
                color: rgb(0.2, 0.2, 0.2),
              });
              xPos += helvetica.widthOfTextAtSize(tok.text, 8) + spaceWidth;
            } else if (tok.type === 'blank') {
              currentPage.drawRectangle({
                x: xPos,
                y: currentY - 11,
                width: BLANK_FIELD_WIDTH,
                height: BLANK_FIELD_HEIGHT,
                color: rgb(0.98, 0.98, 0.98),
                borderColor: rgb(0.6, 0.6, 0.6),
                borderWidth: 0.5,
              });

              const fName = `cbblank_${sIdx}_${qIdx}_${opt.id}_${tok.id}_${fieldCount++}`;
              const blankField = form.createTextField(fName);
              blankField.setText((questionObj.blankValues && questionObj.blankValues[tok.id]) || '');
              blankField.addToPage(currentPage, {
                x: xPos + 1,
                y: currentY - 10,
                width: BLANK_FIELD_WIDTH - 2,
                height: BLANK_FIELD_HEIGHT - 2,
              });
              blankField.setFontSize(8);

              xPos += BLANK_FIELD_WIDTH + spaceWidth;
            } else if (tok.type === 'dropdown') {
              currentPage.drawRectangle({
                x: xPos,
                y: currentY - 11,
                width: DROPDOWN_TOKEN_WIDTH,
                height: BLANK_FIELD_HEIGHT,
                color: rgb(1, 1, 1),
                borderColor: rgb(0.6, 0.6, 0.6),
                borderWidth: 0.5,
              });

              const fName = `cbdrp_${sIdx}_${qIdx}_${opt.id}_${tok.id}_${fieldCount++}`;
              const ddField = form.createDropdown(fName);
              const ddOptions = tok.options && tok.options.length > 0 ? tok.options : ['Select'];
              ddField.setOptions(ddOptions);
              const savedVal = questionObj.blankValues && questionObj.blankValues[tok.id];
              const ddFallback = (savedVal && ddOptions.includes(savedVal)) ? savedVal : ddOptions[0];
              try {
                ddField.select(ddFallback);
              } catch (e) {
                ddField.select(ddOptions[0]);
              }
              ddField.addToPage(currentPage, {
                x: xPos,
                y: currentY - 11,
                width: DROPDOWN_TOKEN_WIDTH,
                height: BLANK_FIELD_HEIGHT,
              });
              ddField.setFontSize(7);

              xPos += DROPDOWN_TOKEN_WIDTH + spaceWidth;
            }
          });

          currentY -= BLANK_LINE_HEIGHT;
        });

        currentY -= 8;
      };

      // Loop through form data structure
      for (let s = 0; s < sections.length; s++) {
        const section = sections[s];
        
        // Add Section Header Block
        checkPageOverflow(40);
        currentPage.drawRectangle({
          x: margin,
          y: currentY - 18,
          width: contentWidth,
          height: 24,
          color: rgb(44/255, 95/255, 138/255),
        });
        currentPage.drawText(section.title, {
          x: margin + 8,
          y: currentY - 12,
          size: 11,
          font: helveticaBold,
          color: rgb(1, 1, 1),
        });
        currentY -= 45;
        
        for (let q = 0; q < section.questions.length; q++) {
          const question = section.questions[q];
          const labelText = question.label || '';
          // Fallback Standard Single-Line Layout pipeline.
          // fitsInline only depends on label text width, not on currentY,
          // so it's safe to compute up front. Everything that depends on
          // currentY (labelY, fieldY, fieldX...) is computed further below,
          // AFTER checkPageOverflow has had a chance to reset currentY.
          const textWidth = helveticaBold.widthOfTextAtSize(labelText, 9);
          const fitsInline = textWidth < 220;
          const targetLabelWidth = 240;

          if (question.type === 'text' || question.type === 'number' || question.type === 'email') {
            const blockHeight = fitsInline ? 32 : 50;
            checkPageOverflow(blockHeight);

            let fieldX, calculatedFieldWidth, fieldY;
            if (fitsInline) {
              currentPage.drawText(labelText, {
                x: margin,
                y: currentY - 10,
                size: 9,
                font: helveticaBold,
                color: rgb(0.1, 0.1, 0.1),
              });
              fieldX = margin + targetLabelWidth;
              calculatedFieldWidth = contentWidth - targetLabelWidth - 10;
              fieldY = currentY - 16;
            } else {
              drawWrappedLabel(labelText, margin, contentWidth, 9, helveticaBold, rgb(0.1, 0.1, 0.1));
              currentY -= 18;
              fieldX = margin;
              calculatedFieldWidth = contentWidth;
              fieldY = currentY - 16;
            }

            currentPage.drawRectangle({
              x: fieldX,
              y: fieldY,
              width: calculatedFieldWidth,
              height: 18,
              color: rgb(0.98, 0.98, 0.98),
              borderColor: rgb(0.7, 0.7, 0.7),
              borderWidth: 0.5
            });

            const fieldName = `txt_${s}_${q}_${fieldCount++}`;
            const field = form.createTextField(fieldName);
            field.setText(question.value || '');
            field.addToPage(currentPage, {
              x: fieldX + 2,
              y: fieldY + 2,
              width: calculatedFieldWidth - 4,
              height: 14,
            });
            // Pin an explicit font size (rather than leaving it at pdf-lib's
            // default "auto" size of 0) so text renders at a consistent size
            // across every field, regardless of which PDF viewer is used.
            field.setFontSize(9);

            // Give every row the same trailing gap (32pt) whether the label sat
            // inline or wrapped onto its own line above the box. Previously the
            // wrapped case only used 18pt here (on top of an already-consumed
            // 18pt for the label line = 36 total against a reserved 50), which
            // left almost no breathing room before the next question - boxes
            // looked like they were touching. Now both cases consume the full
            // 50pt reserved by checkPageOverflow, matching the comfortable
            // ~20pt gap already seen between simple inline rows.
            currentY -= 32;

          } else if (question.type === 'dropdown') {
            const blockHeight = fitsInline ? 32 : 50;
            checkPageOverflow(blockHeight);

            let fieldX, calculatedFieldWidth, fieldY;
            if (fitsInline) {
              currentPage.drawText(labelText, {
                x: margin,
                y: currentY - 10,
                size: 9,
                font: helveticaBold,
                color: rgb(0.1, 0.1, 0.1),
              });
              fieldX = margin + targetLabelWidth;
              calculatedFieldWidth = contentWidth - targetLabelWidth - 10;
              fieldY = currentY - 16;
            } else {
              drawWrappedLabel(labelText, margin, contentWidth, 9, helveticaBold, rgb(0.1, 0.1, 0.1));
              currentY -= 18;
              fieldX = margin;
              calculatedFieldWidth = contentWidth;
              fieldY = currentY - 16;
            }

            currentPage.drawRectangle({
              x: fieldX,
              y: fieldY,
              width: calculatedFieldWidth,
              height: 18,
              color: rgb(1, 1, 1),
              borderColor: rgb(0.6, 0.6, 0.6),
              borderWidth: 0.75
            });

            const arrowX = fieldX + calculatedFieldWidth - 14;
            const arrowY = fieldY + 6;
            const svgPathData = `M ${arrowX} ${arrowY + 4} L ${arrowX + 6} ${arrowY + 4} L ${arrowX + 3} ${arrowY} Z`;
            currentPage.drawSvgPath(svgPathData, {
              color: rgb(0.3, 0.3, 0.3),
            });
            
            const fieldName = `drp_${s}_${q}_${fieldCount++}`;
            const field = form.createDropdown(fieldName);
            
            const selectOptions = question.options && question.options.length > 0 ? question.options : ['Select'];
            field.setOptions(selectOptions);
            
            const fallbackValue = (question.value && selectOptions.includes(question.value))
              ? question.value
              : (selectOptions.includes('Select') ? 'Select' : selectOptions[0]);
            try {
              field.select(fallbackValue);
            } catch (e) {
              field.select(selectOptions[0]);
            }
            
            field.addToPage(currentPage, {
              x: fieldX,
              y: fieldY,
              width: calculatedFieldWidth, 
              height: 18,
            });
            field.setFontSize(9);

            // Give every row the same trailing gap (32pt) whether the label sat
            // inline or wrapped onto its own line above the box. Previously the
            // wrapped case only used 18pt here (on top of an already-consumed
            // 18pt for the label line = 36 total against a reserved 50), which
            // left almost no breathing room before the next question - boxes
            // looked like they were touching. Now both cases consume the full
            // 50pt reserved by checkPageOverflow, matching the comfortable
            // ~20pt gap already seen between simple inline rows.
            currentY -= 32;

          } else if (question.type === 'checkbox') {
            const options = question.options || [];
            
            checkPageOverflow(25 + (options.length * 18));
            drawWrappedLabel(labelText, margin, contentWidth, 9, helveticaBold, rgb(0.1, 0.1, 0.1));
            currentY -= 24;
            
            options.forEach((opt) => {
              checkPageOverflow(18);
              
              const fieldName = `chk_${s}_${q}_${fieldCount++}`;
              const field = form.createCheckBox(fieldName);
              if ((question.value || []).includes(opt)) field.check();
              
              field.addToPage(currentPage, {
                x: margin + 12,
                y: currentY - 12,
                width: 12,
                height: 12,
                borderColor: rgb(0.4, 0.4, 0.4),
                borderWidth: 0.5,
              });
              
              currentPage.drawText(opt, {
                x: margin + 32,
                y: currentY - 10,
                size: 9,
                font: helvetica,
                color: rgb(0.2, 0.2, 0.2),
              });
              
              currentY -= 18;
            });
            currentY -= 10;

          } else if (question.type === 'checkboxgroup') {
            checkPageOverflow(25);
            drawWrappedLabel(labelText, margin, contentWidth, 9, helveticaBold, rgb(0.1, 0.1, 0.1));
            currentY -= 20;
            
            const groups = question.groups || [];
            for (let g = 0; g < groups.length; g++) {
              const group = groups[g];
              
              checkPageOverflow(20 + (group.options.length * 18));
              currentPage.drawText(group.label + ':', {
                x: margin + 10,
                y: currentY - 10,
                size: 9,
                font: helveticaBold,
                color: rgb(0.4, 0.4, 0.4),
              });
              currentY -= 22;
              
              group.options.forEach((opt) => {
                checkPageOverflow(18);
                
                const fieldName = `chkg_${s}_${q}_${g}_${fieldCount++}`;
                const field = form.createCheckBox(fieldName);
                if ((question.value && question.value[group.name] || []).includes(opt)) field.check();
                
                field.addToPage(currentPage, {
                  x: margin + 22,
                  y: currentY - 11,
                  width: 11,
                  height: 11,
                  borderColor: rgb(0.5, 0.5, 0.5),
                  borderWidth: 0.5,
                });
                
                currentPage.drawText(opt, {
                  x: margin + 40,
                  y: currentY - 9,
                  size: 8,
                  font: helvetica,
                  color: rgb(0.2, 0.2, 0.2),
                });
                
                currentY -= 18;
              });
              currentY -= 5;
            }

          } else if (question.type === 'subgrid') {
            // Some subgrid fields carry no explicit label (e.g. the option/days
            // pairs under "New Hire Email Reminder", "New Hire Enrollment window
            // ends on", etc). Falling back to the raw internal field.name
            // (e.g. "medical_nh_window_option") produced unreadable text in the
            // PDF. Build a readable label instead: prefer field.label, then a
            // cleaned-up placeholder, then a humanized version of field.name.
            const humanizeName = (name) => {
              if (!name) return '';
              return name
                .replace(/[_-]+/g, ' ')
                .trim()
                .replace(/\b\w/g, (c) => c.toUpperCase());
            };
            const subFieldLabel = (field) => {
              if (field.label) return field.label;
              if (field.placeholder) return field.placeholder.replace(/^Enter\s+/i, '');
              return humanizeName(field.name);
            };

            checkPageOverflow(63);
            drawWrappedLabel(labelText, margin, contentWidth, 9, helveticaBold, rgb(0.1, 0.1, 0.1));
            // Was 25 - left only ~7pt between this heading and the field
            // labels below it ("Name"/"Email"/"Phone Number"), so they
            // visually ran into each other. 34 gives a clear ~16pt gap,
            // matching the spacing used between other questions.
            currentY -= 34;
            
            const fields = question.fields || [];
            const subWidth = contentWidth / Math.max(fields.length, 1) - 8;
            let xPos = margin;
            
            fields.forEach((field) => {
              const fieldLabel = subFieldLabel(field);
              currentPage.drawText(fieldLabel, {
                x: xPos,
                y: currentY + 8,
                size: 8,
                font: helveticaBold,
                color: rgb(0.15, 0.15, 0.15),
              });

              if (field.type === 'dropdown') {
                // Render as a real interactive dropdown instead of a text box,
                // matching how top-level dropdown questions are rendered.
                // Box shifted down (currentY-15 instead of currentY-10) so its
                // top edge clears the label's baseline/descenders instead of
                // sitting right on top of it.
                currentPage.drawRectangle({
                  x: xPos,
                  y: currentY - 15,
                  width: subWidth,
                  height: 18,
                  color: rgb(1, 1, 1),
                  borderColor: rgb(0.6, 0.6, 0.6),
                  borderWidth: 0.75
                });

                const arrowX = xPos + subWidth - 14;
                const arrowY = currentY - 9;
                const svgPathData = `M ${arrowX} ${arrowY} L ${arrowX + 6} ${arrowY} L ${arrowX + 3} ${arrowY - 4} Z`;
                currentPage.drawSvgPath(svgPathData, {
                  color: rgb(0.3, 0.3, 0.3),
                });

                const fName = `subdrp_${s}_${q}_${fieldCount++}`;
                const dropField = form.createDropdown(fName);
                const subOptions = field.options && field.options.length > 0 ? field.options : ['Select'];
                dropField.setOptions(subOptions);
                const subFallback = (field.value && subOptions.includes(field.value))
                  ? field.value
                  : subOptions[0];
                try {
                  dropField.select(subFallback);
                } catch (e) {
                  dropField.select(subOptions[0]);
                }
                dropField.addToPage(currentPage, {
                  x: xPos,
                  y: currentY - 15,
                  width: subWidth,
                  height: 18,
                });
                dropField.setFontSize(9);
              } else {
                currentPage.drawRectangle({
                  x: xPos,
                  y: currentY - 15,
                  width: subWidth,
                  height: 18, 
                  color: rgb(0.98, 0.98, 0.98),
                  borderColor: rgb(0.7, 0.7, 0.7),
                  borderWidth: 0.5
                });

                const fName = `sub_${s}_${q}_${fieldCount++}`;
                const textField = form.createTextField(fName);
                textField.setText(field.value || '');
                textField.addToPage(currentPage, {
                  x: xPos + 2,
                  y: currentY - 13,
                  width: subWidth - 4,
                  height: 14,
                });
                textField.setFontSize(9);
              }
              
              xPos += subWidth + 8;
            });
            currentY -= 39;

          } else if (question.type === 'radioWithBlanks') {
            checkPageOverflow(30);
            drawWrappedLabel(labelText, margin, contentWidth, 9, helveticaBold, rgb(0.1, 0.1, 0.1));
            currentY -= 26;

            const radioOptions = question.options || [];
            const radioGroupName = `radio_${s}_${q}_${fieldCount++}`;
            const radioGroup = form.createRadioGroup(radioGroupName);

            radioOptions.forEach((opt) => {
              renderOptionWithBlanks(s, q, question, opt, radioGroup, 22);
            });

            try {
              if (question.value && radioOptions.some((o) => o.id === question.value)) {
                radioGroup.select(question.value);
              }
            } catch (e) {
              // no selection yet - leave unselected
            }

            currentY -= 4;

          } else if (question.type === 'checkboxWithBlanks') {
            checkPageOverflow(30);
            drawWrappedLabel(labelText, margin, contentWidth, 9, helveticaBold, rgb(0.1, 0.1, 0.1));
            currentY -= 26;

            const cbOptions = question.options || [];
            cbOptions.forEach((opt) => {
              renderCheckboxOptionWithBlanks(s, q, question, opt, 22);
            });

            currentY -= 4;

          } else if (question.type === 'yesNoTable') {
            // Sized the same way as a normal top-level dropdown (reusing the
            // fitsInline/targetLabelWidth already computed above for this
            // question) instead of a hardcoded 120pt box, so this Yes/No
            // selector looks the same size as every other dropdown in the
            // PDF (e.g. "Coverage Rounding?") rather than noticeably smaller.
            const ynBlockHeight = fitsInline ? 32 : 50;
            checkPageOverflow(ynBlockHeight);

            let ynFieldX, ynFieldWidth, ynFieldY;
            if (fitsInline) {
              currentPage.drawText(labelText, {
                x: margin,
                y: currentY - 10,
                size: 9,
                font: helveticaBold,
                color: rgb(0.1, 0.1, 0.1),
              });
              ynFieldX = margin + targetLabelWidth;
              ynFieldWidth = contentWidth - targetLabelWidth - 10;
              ynFieldY = currentY - 16;
            } else {
              drawWrappedLabel(labelText, margin, contentWidth, 9, helveticaBold, rgb(0.1, 0.1, 0.1));
              currentY -= 18;
              ynFieldX = margin;
              ynFieldWidth = contentWidth;
              ynFieldY = currentY - 16;
            }

            currentPage.drawRectangle({
              x: ynFieldX,
              y: ynFieldY,
              width: ynFieldWidth,
              height: 18,
              color: rgb(1, 1, 1),
              borderColor: rgb(0.6, 0.6, 0.6),
              borderWidth: 0.75
            });

            const ynArrowX = ynFieldX + ynFieldWidth - 14;
            const ynArrowY = ynFieldY + 6;
            currentPage.drawSvgPath(`M ${ynArrowX} ${ynArrowY + 4} L ${ynArrowX + 6} ${ynArrowY + 4} L ${ynArrowX + 3} ${ynArrowY} Z`, {
              color: rgb(0.3, 0.3, 0.3),
            });

            const ynFieldName = `ynt_${s}_${q}_${fieldCount++}`;
            const ynField = form.createDropdown(ynFieldName);
            const ynOptions = ['Select', 'Yes', 'No'];
            ynField.setOptions(ynOptions);
            const ynFallback = ynOptions.includes(question.value) ? question.value : 'Select';
            try {
              ynField.select(ynFallback);
            } catch (e) {
              ynField.select('Select');
            }
            ynField.addToPage(currentPage, {
              x: ynFieldX,
              y: ynFieldY,
              width: ynFieldWidth,
              height: 18,
            });
            ynField.setFontSize(9);
            currentY -= 32;

            // "Add New" only exists as an interactive control in the web
            // app - a PDF can't have a live add-row button - so the PDF
            // instead prints exactly the rows already added on the web form
            // (however many that is: 2, 5, 9, whatever was clicked into
            // existence), each with its own entered (or still-blank)
            // percent/age values carried straight through.
            const ynRows = question.rows || [];
            if (question.value === 'Yes' && ynRows.length > 0) {
              checkPageOverflow(24);
              currentPage.drawText('S.No', {
                x: margin, y: currentY - 10, size: 8, font: helveticaBold, color: rgb(0.3, 0.3, 0.3),
              });
              currentPage.drawText('Benefit reduces %', {
                x: margin + 50, y: currentY - 10, size: 8, font: helveticaBold, color: rgb(0.3, 0.3, 0.3),
              });
              currentPage.drawText('At Age', {
                x: margin + 220, y: currentY - 10, size: 8, font: helveticaBold, color: rgb(0.3, 0.3, 0.3),
              });
              currentY -= 20;

              ynRows.forEach((row, idx) => {
                checkPageOverflow(24);

                currentPage.drawText(String(idx + 1), {
                  x: margin, y: currentY - 10, size: 9, font: helvetica, color: rgb(0.2, 0.2, 0.2),
                });

                currentPage.drawRectangle({
                  x: margin + 50, y: currentY - 15, width: 140, height: 18,
                  color: rgb(0.98, 0.98, 0.98), borderColor: rgb(0.7, 0.7, 0.7), borderWidth: 0.5
                });
                const pctField = form.createTextField(`ynt_pct_${s}_${q}_${idx}_${fieldCount++}`);
                pctField.setText(row.percent || '');
                pctField.addToPage(currentPage, {
                  x: margin + 52, y: currentY - 13, width: 136, height: 14,
                });
                pctField.setFontSize(9);

                currentPage.drawRectangle({
                  x: margin + 220, y: currentY - 15, width: 100, height: 18,
                  color: rgb(0.98, 0.98, 0.98), borderColor: rgb(0.7, 0.7, 0.7), borderWidth: 0.5
                });
                const ageField = form.createTextField(`ynt_age_${s}_${q}_${idx}_${fieldCount++}`);
                ageField.setText(row.age || '');
                ageField.addToPage(currentPage, {
                  x: margin + 222, y: currentY - 13, width: 96, height: 14,
                });
                ageField.setFontSize(9);

                currentY -= 24;
              });
              currentY -= 8;
            }
          }
        }
        currentY -= 15; 
      }
      
      // Force pdf-lib to regenerate proper appearance streams for every
      // field. Without this, some viewers/extractors that don't run their
      // own "NeedAppearances" pass can fall back to showing a field's
      // internal name instead of its selected value/blank state — this is
      // what caused dropdowns to display raw names like
      // "medical_nh_window_option" instead of a real dropdown.
      form.updateFieldAppearances(helvetica);

      // Footer page index tracking setup loops
      const pages = pdfDoc.getPages();
      pages.forEach((p, idx) => {
        const footerText = `Page ${idx + 1} of ${pages.length} — Generated from Client Implementation Clarifications Form`;
        p.drawText(footerText, {
          x: (width - helvetica.widthOfTextAtSize(footerText, 8)) / 2,
          y: 25,
          size: 8,
          font: helvetica,
          color: rgb(0.6, 0.6, 0.6),
        });
      });
      
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'Client_Clarification_Form.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      btn.innerHTML = originalText;
      btn.disabled = false;
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Error generating PDF: ' + error.message);
    }
  };

  // ===== RENDER QUESTION =====
  const renderQuestion = (question, sectionId) => {
    switch (question.type) {
      case 'text':
        return (
          <div key={question.id} className="question">
            <div className="question-header">
              <label 
                contentEditable 
                suppressContentEditableWarning 
                onBlur={(e) => updateQuestionLabel(sectionId, question.id, e.target.textContent)}
              >
                {question.label}
              </label>
              <button 
                className="delete-btn" 
                onClick={() => deleteQuestion(sectionId, question.id)}
                title="Delete question"
              >
                ✕
              </button>
            </div>
            <input
              type="text"
              placeholder={question.placeholder}
              value={question.value || ''}
              onChange={(e) => updateField(sectionId, question.id, e.target.value)}
            />
          </div>
        );

      case 'number':
        return (
          <div key={question.id} className="question">
            <div className="question-header">
              <label 
                contentEditable 
                suppressContentEditableWarning 
                onBlur={(e) => updateQuestionLabel(sectionId, question.id, e.target.textContent)}
              >
                {question.label}
              </label>
              <button 
                className="delete-btn" 
                onClick={() => deleteQuestion(sectionId, question.id)}
                title="Delete question"
              >
                ✕
              </button>
            </div>
            <input
              type="number"
              placeholder={question.placeholder}
              value={question.value || ''}
              onChange={(e) => updateField(sectionId, question.id, e.target.value)}
            />
          </div>
        );

      case 'email':
        return (
          <div key={question.id} className="question">
            <div className="question-header">
              <label 
                contentEditable 
                suppressContentEditableWarning 
                onBlur={(e) => updateQuestionLabel(sectionId, question.id, e.target.textContent)}
              >
                {question.label}
              </label>
              <button 
                className="delete-btn" 
                onClick={() => deleteQuestion(sectionId, question.id)}
                title="Delete question"
              >
                ✕
              </button>
            </div>
            <input
              type="email"
              placeholder={question.placeholder}
              value={question.value || ''}
              onChange={(e) => updateField(sectionId, question.id, e.target.value)}
            />
          </div>
        );

      case 'dropdown':
        return (
          <div key={question.id} className="question">
            <div className="question-header">
              <label 
                contentEditable 
                suppressContentEditableWarning 
                onBlur={(e) => updateQuestionLabel(sectionId, question.id, e.target.textContent)}
              >
                {question.label}
              </label>
              <button 
                className="delete-btn" 
                onClick={() => deleteQuestion(sectionId, question.id)}
                title="Delete question"
              >
                ✕
              </button>
            </div>
            <select
              value={question.value || ''}
              onChange={(e) => updateField(sectionId, question.id, e.target.value)}
            >
              {question.options.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );

      case 'checkbox':
        return (
          <div key={question.id} className="question">
            <div className="question-header">
              <label 
                contentEditable 
                suppressContentEditableWarning 
                onBlur={(e) => updateQuestionLabel(sectionId, question.id, e.target.textContent)}
              >
                {question.label}
              </label>
              <button 
                className="delete-btn" 
                onClick={() => deleteQuestion(sectionId, question.id)}
                title="Delete question"
              >
                ✕
              </button>
            </div>
            <div className="checkbox-group">
              {question.options.map((option, index) => (
                <label key={index} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={(question.value || []).includes(option)}
                    onChange={(e) => updateCheckbox(sectionId, question.id, option, e.target.checked)}
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        );

      case 'checkboxgroup':
        return (
          <div key={question.id} className="question">
            <div className="question-header">
              <label 
                contentEditable 
                suppressContentEditableWarning 
                onBlur={(e) => updateQuestionLabel(sectionId, question.id, e.target.textContent)}
              >
                {question.label}
              </label>
              <button 
                className="delete-btn" 
                onClick={() => deleteQuestion(sectionId, question.id)}
                title="Delete question"
              >
                ✕
              </button>
            </div>
            <div className="subgrid">
              {question.groups.map((group, groupIndex) => (
                <div key={groupIndex} className="checkbox-group-wrapper">
                  <label className="sub-label">{group.label}</label>
                  <div className="checkbox-group">
                    {group.options.map((option, optionIndex) => (
                      <label key={optionIndex} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={(question.value && question.value[group.name] || []).includes(option)}
                          onChange={(e) => updateCheckboxGroup(
                            sectionId, 
                            question.id, 
                            group.name, 
                            option, 
                            e.target.checked
                          )}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'yesNoTable':
        return (
          <div key={question.id} className="question">
            <div className="question-header">
              <label 
                contentEditable 
                suppressContentEditableWarning 
                onBlur={(e) => updateQuestionLabel(sectionId, question.id, e.target.textContent)}
              >
                {question.label}
              </label>
              <button 
                className="delete-btn" 
                onClick={() => deleteQuestion(sectionId, question.id)}
                title="Delete question"
              >
                ✕
              </button>
            </div>
            <select
              value={question.value || ''}
              onChange={(e) => updateYesNoTableValue(sectionId, question.id, e.target.value)}
            >
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>

            {question.value === 'Yes' && (
              <div className="yesno-table-wrapper">
                <div className="yesno-table-toolbar">
                  <button
                    type="button"
                    className="yesno-add-btn"
                    onClick={() => addYesNoTableRow(sectionId, question.id)}
                  >
                    ➕ Add New
                  </button>
                </div>
                <table className="yesno-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>The Benefit will reduce %</th>
                      <th>At Age</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(question.rows || []).length === 0 && (
                      <tr>
                        <td colSpan="4" className="yesno-empty-row">No rows yet - click "Add New"</td>
                      </tr>
                    )}
                    {(question.rows || []).map((row, idx) => (
                      <tr key={row.id}>
                        <td>{idx + 1}</td>
                        <td>
                          <input
                            type="number"
                            placeholder="%"
                            value={row.percent}
                            onChange={(e) => updateYesNoTableRow(sectionId, question.id, row.id, 'percent', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            placeholder="Age"
                            value={row.age}
                            onChange={(e) => updateYesNoTableRow(sectionId, question.id, row.id, 'age', e.target.value)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="yesno-delete-btn"
                            onClick={() => deleteYesNoTableRow(sectionId, question.id, row.id)}
                            title="Delete row"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'radioWithBlanks':
        return (
          <div key={question.id} className="question">
            <div className="question-header">
              <label 
                contentEditable 
                suppressContentEditableWarning 
                onBlur={(e) => updateQuestionLabel(sectionId, question.id, e.target.textContent)}
              >
                {question.label}
              </label>
              <button 
                className="delete-btn" 
                onClick={() => deleteQuestion(sectionId, question.id)}
                title="Delete question"
              >
                ✕
              </button>
            </div>
            <div className="radio-blank-group">
              {question.options.map((opt) => (
                <label key={opt.id} className="radio-blank-option">
                  <input
                    type="radio"
                    name={question.name}
                    checked={question.value === opt.id}
                    onChange={() => updateRadioSelection(sectionId, question.id, opt.id)}
                  />
                  <span className="radio-blank-text">
                    {opt.segments.map((seg, i) => (
                      seg.type === 'text' ? (
                        <span key={i}>{seg.text}{' '}</span>
                      ) : (
                        <input
                          key={i}
                          type="number"
                          className="inline-blank-input"
                          placeholder={seg.placeholder || '#'}
                          value={(question.blankValues && question.blankValues[seg.id]) || ''}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => updateRadioBlankValue(sectionId, question.id, seg.id, e.target.value)}
                        />
                      )
                    ))}
                  </span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'checkboxWithBlanks':
        return (
          <div key={question.id} className="question">
            <div className="question-header">
              <label
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => updateQuestionLabel(sectionId, question.id, e.target.textContent)}
              >
                {question.label}
              </label>
              <button
                className="delete-btn"
                onClick={() => deleteQuestion(sectionId, question.id)}
                title="Delete question"
              >
                ✕
              </button>
            </div>
            <div className="checkbox-blank-group">
              {question.options.map((opt) => (
                <label key={opt.id} className="checkbox-blank-option">
                  <input
                    type="checkbox"
                    checked={(question.value || []).includes(opt.id)}
                    onChange={(e) => updateCheckboxWithBlanksToggle(sectionId, question.id, opt.id, e.target.checked)}
                  />
                  <span className="checkbox-blank-text">
                    {opt.segments.map((seg, i) => {
                      if (seg.type === 'text') {
                        return <span key={i}>{seg.text}{' '}</span>;
                      }
                      if (seg.type === 'blank') {
                        return (
                          <input
                            key={i}
                            type="text"
                            className="inline-blank-input"
                            placeholder={seg.placeholder || '#'}
                            value={(question.blankValues && question.blankValues[seg.id]) || ''}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => updateCheckboxBlankValue(sectionId, question.id, seg.id, e.target.value)}
                          />
                        );
                      }
                      if (seg.type === 'dropdown') {
                        return (
                          <select
                            key={i}
                            className="inline-blank-select"
                            value={(question.blankValues && question.blankValues[seg.id]) || ''}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => updateCheckboxBlankValue(sectionId, question.id, seg.id, e.target.value)}
                          >
                            {(seg.options || []).map((o, oi) => (
                              <option key={oi} value={o}>{o}</option>
                            ))}
                          </select>
                        );
                      }
                      return null;
                    })}
                  </span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'subgrid':
        return (
          <div key={question.id} className="question">
            <div className="question-header">
              <label 
                contentEditable 
                suppressContentEditableWarning 
                onBlur={(e) => updateQuestionLabel(sectionId, question.id, e.target.textContent)}
              >
                {question.label}
              </label>
              <button 
                className="delete-btn" 
                onClick={() => deleteQuestion(sectionId, question.id)}
                title="Delete question"
              >
                ✕
              </button>
            </div>
            <div className="subgrid">
              {question.fields.map((field) => (
                <div key={field.id} className="subgrid-item">
                  {field.type === 'dropdown' ? (
                    <select
                      value={field.value || ''}
                      onChange={(e) => {
                        setFormData(prev => {
                          const newSections = prev.sections.map(section => {
                            if (section.id !== sectionId) return section;
                            return {
                              ...section,
                              questions: section.questions.map(q => {
                                if (q.id !== question.id) return q;
                                return {
                                  ...q,
                                  fields: q.fields.map(f => {
                                    if (f.id !== field.id) return f;
                                    return { ...f, value: e.target.value };
                                  })
                                };
                              })
                            };
                          });
                          return { ...prev, sections: newSections };
                        });
                      }}
                    >
                      {field.options.map((option, index) => (
                        <option key={index} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type || 'text'}
                      placeholder={field.placeholder || ''}
                      value={field.value || ''}
                      onChange={(e) => {
                        setFormData(prev => {
                          const newSections = prev.sections.map(section => {
                            if (section.id !== sectionId) return section;
                            return {
                              ...section,
                              questions: section.questions.map(q => {
                                if (q.id !== question.id) return q;
                                return {
                                  ...q,
                                  fields: q.fields.map(f => {
                                    if (f.id !== field.id) return f;
                                    return { ...f, value: e.target.value };
                                  })
                                };
                              })
                            };
                          });
                          return { ...prev, sections: newSections };
                        });
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ===== RENDER ADD QUESTION DIALOG =====
  const renderAddQuestionDialog = () => {
    const questionTypes = [
      { value: 'text', label: '📝 Text' },
      { value: 'number', label: '🔢 Number' },
      { value: 'email', label: '📧 Email' },
      { value: 'dropdown', label: '📋 Dropdown' },
      { value: 'checkbox', label: '☑️ Checkbox' },
      { value: 'checkboxgroup', label: '📋 Checkbox Group' },
      { value: 'subgrid', label: '📊 Subgrid' }
    ];

    // If someone switches the type dropdown to "Subgrid" while already
    // inside the dialog (rather than relying on the section-aware default
    // set by openAddQuestionDialog), still apply the Carrier Name/Email/
    // Phone Number format when the target section is a Carriers section.
    const currentSection = formData.sections.find(s => s.id === addQuestionSectionId);
    const isCarrierSection = isCarrierSectionTitle(currentSection && currentSection.title);

    // CHANGED: the overlay no longer has an onClick that closes the dialog.
    // Previously clicking anywhere outside the box (or finishing a text
    // selection / mouse drag outside it) closed this dialog and threw away
    // what was typed. Now it only closes via the Cancel button (or after
    // "Add Question"), same as the Add Section / Copy Question dialogs.
    return (
      <div className="modal-overlay">
        <div className="modal add-question-modal" onClick={(e) => e.stopPropagation()}>
          <h3>➕ Add Question</h3>
          <p>Customize your question before adding:</p>

          <div className="editor-field">
            <label>Insert Position:</label>
            <select
              className="editor-input"
              value={addQuestionPosition !== null ? addQuestionPosition : 'end'}
              onChange={(e) => {
                const val = e.target.value;
                setAddQuestionPosition(val === 'end' ? null : parseInt(val));
              }}
            >
              <option value="end">At the End</option>
              {currentSection && currentSection.questions.map((q, idx) => (
                <option key={q.id} value={idx}>After "{q.label}"</option>
              ))}
            </select>
          </div>

          <div className="editor-field">
            <label>Question Type:</label>
            <select 
              className="editor-input"
              value={newQuestionData.type}
              onChange={(e) => {
                const type = e.target.value;
                let fields = [];

                if (type === 'subgrid' && isCarrierSection) {
                  fields = getCarrierDefaultFields();
                }

                setNewQuestionData({ 
                  ...newQuestionData, 
                  type: type,
                  options: ['Option 1', 'Option 2', 'Option 3'],
                  fields: fields
                });
              }}
            >
              {questionTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="editor-field">
            <label>Question Label:</label>
            <input
              type="text"
              className="editor-input"
              placeholder="Enter question label"
              value={newQuestionData.label}
              onChange={(e) => setNewQuestionData({ ...newQuestionData, label: e.target.value })}
            />
          </div>

          {(newQuestionData.type === 'text' || newQuestionData.type === 'number' || newQuestionData.type === 'email') && (
            <div className="editor-field">
              <label>Placeholder:</label>
              <input
                type="text"
                className="editor-input"
                placeholder="Enter placeholder text"
                value={newQuestionData.placeholder}
                onChange={(e) => setNewQuestionData({ ...newQuestionData, placeholder: e.target.value })}
              />
            </div>
          )}

          {(newQuestionData.type === 'dropdown' || newQuestionData.type === 'checkbox') && (
            <div className="editor-options">
              <label>Options:</label>
              <div className="options-list">
                {newQuestionData.options.map((option, index) => (
                  <div key={index} className="option-item">
                    <input
                      type="text"
                      className="option-input"
                      value={option}
                      onChange={(e) => updateNewQuestionOption(index, e.target.value)}
                    />
                    <button 
                      className="option-delete-btn"
                      onClick={() => deleteNewQuestionOption(index)}
                      title="Delete option"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button 
                className="option-add-btn"
                onClick={addNewQuestionOption}
              >
                ➕ Add Option
              </button>
            </div>
          )}

          {newQuestionData.type === 'subgrid' && (
            <div className="editor-field">
              <label>Subgrid Fields:</label>
              {newQuestionData.fields.map((field, index) => (
                <div key={index} className="subgrid-field-item">
                  <div className="subgrid-field-row">
                    <input
                      type="text"
                      className="editor-input"
                      placeholder="Field label"
                      value={field.label || ''}
                      onChange={(e) => updateSubgridField(index, 'label', e.target.value)}
                    />
                    <select
                      className="editor-input"
                      value={field.type || 'text'}
                      onChange={(e) => updateSubgridField(index, 'type', e.target.value)}
                      style={{ width: '120px' }}
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="email">Email</option>
                      <option value="dropdown">Dropdown</option>
                    </select>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteSubgridField(index)}
                      title="Delete field"
                    >
                      ✕
                    </button>
                  </div>
                  {field.type === 'dropdown' && (
                    <div className="subgrid-field-options">
                      <input
                        type="text"
                        className="editor-input"
                        placeholder="Dropdown options (comma separated)"
                        value={field.options ? field.options.join(', ') : ''}
                        onChange={(e) => updateSubgridField(index, 'options', e.target.value.split(',').map(s => s.trim()))}
                      />
                    </div>
                  )}
                </div>
              ))}
              <button 
                className="option-add-btn"
                onClick={addSubgridField}
              >
                ➕ Add Field
              </button>
            </div>
          )}

          {newQuestionData.type === 'checkboxgroup' && (
            <div className="editor-field">
              <label>Checkbox Groups:</label>
              {newQuestionData.fields.map((group, index) => (
                <div key={index} className="checkbox-group-item">
                  <div className="subgrid-field-row">
                    <input
                      type="text"
                      className="editor-input"
                      placeholder="Group label"
                      value={group.label || ''}
                      onChange={(e) => updateCheckboxGroupField(index, 'label', e.target.value)}
                    />
                    <button 
                      className="delete-btn"
                      onClick={() => deleteSubgridField(index)}
                      title="Delete group"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="editor-options">
                    <label>Options for "{group.label || 'Group'}":</label>
                    <div className="options-list">
                      {group.options && group.options.map((option, optIndex) => (
                        <div key={optIndex} className="option-item">
                          <input
                            type="text"
                            className="option-input"
                            value={option}
                            onChange={(e) => updateCheckboxGroupOption(index, optIndex, e.target.value)}
                          />
                          <button 
                            className="option-delete-btn"
                            onClick={() => deleteCheckboxGroupOption(index, optIndex)}
                            title="Delete option"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <button 
                      className="option-add-btn"
                      onClick={() => addCheckboxGroupOption(index)}
                    >
                      ➕ Add Option
                    </button>
                  </div>
                </div>
              ))}
              <button 
                className="option-add-btn"
                onClick={addCheckboxGroupField}
              >
                ➕ Add Group
              </button>
            </div>
          )}

          <div className="modal-actions">
            <button 
              className="modal-btn cancel" 
              onClick={() => {
                setShowAddQuestionDialog(false);
                setAddQuestionSectionId(null);
                setAddQuestionPosition(null);
              }}
            >
              Cancel
            </button>
            <button 
              className="modal-btn save" 
              onClick={addQuestionToSection}
            >
              ➕ Add Question
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===== RENDER COPY QUESTION DIALOG =====
  const renderCopyQuestionDialog = () => {
    // Step 1: narrow by section (optional - "All Sections" shows everything).
    // Step 2: narrow further with a live text search on the question label.
    // Keeps the picker list short instead of dumping every question in the
    // form into one long flat dropdown.
    const sectionsToShow = copyQuestionSectionFilter
      ? formData.sections.filter(s => s.id === copyQuestionSectionFilter)
      : formData.sections;

    const searchLower = copyQuestionSearchTerm.trim().toLowerCase();

    const filteredQuestionsFlat = sectionsToShow.flatMap(section =>
      section.questions
        .filter(q => !searchLower || q.label.toLowerCase().includes(searchLower))
        .map(q => ({ sectionTitle: section.title, question: q }))
    );

    const targetSection = formData.sections.find(s => s.id === copyQuestionTargetSectionId);

    return (
      <div className="modal-overlay">
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>📋 Copy Question</h3>
          <p>Narrow it down, then pick the question to copy in here:</p>

          <div className="editor-field">
            <label>Insert Position:</label>
            <select
              className="editor-input"
              value={copyQuestionPosition !== null ? copyQuestionPosition : 'end'}
              onChange={(e) => {
                const val = e.target.value;
                setCopyQuestionPosition(val === 'end' ? null : parseInt(val));
              }}
            >
              <option value="end">At the End</option>
              {targetSection && targetSection.questions.map((q, idx) => (
                <option key={q.id} value={idx}>After "{q.label}"</option>
              ))}
            </select>
          </div>

          <div className="editor-field">
            <label>Filter by section:</label>
            <select
              className="editor-input"
              value={copyQuestionSectionFilter}
              onChange={(e) => {
                setCopyQuestionSectionFilter(e.target.value);
                setCopyQuestionSourceId('');
              }}
            >
              <option value="">All Sections</option>
              {formData.sections.map((section) => (
                <option key={section.id} value={section.id}>{section.title}</option>
              ))}
            </select>
          </div>

          <div className="editor-field">
            <label>Search questions:</label>
            <input
              type="text"
              className="editor-input"
              placeholder="Type part of a question label..."
              value={copyQuestionSearchTerm}
              onChange={(e) => {
                setCopyQuestionSearchTerm(e.target.value);
                setCopyQuestionSourceId('');
              }}
            />
          </div>

          <div className="editor-field">
            <label>Question to copy ({filteredQuestionsFlat.length} match{filteredQuestionsFlat.length === 1 ? '' : 'es'}):</label>
            <select
              className="editor-input"
              value={copyQuestionSourceId}
              onChange={(e) => setCopyQuestionSourceId(e.target.value)}
            >
              <option value="">Select a question</option>
              {filteredQuestionsFlat.map(({ sectionTitle, question }) => (
                <option key={question.id} value={question.id}>
                  {sectionTitle} — {question.label}
                </option>
              ))}
            </select>
          </div>

          <div className="modal-actions">
            <button
              className="modal-btn cancel"
              onClick={() => {
                setShowCopyQuestionDialog(false);
                setCopyQuestionTargetSectionId(null);
                setCopyQuestionSourceId('');
                setCopyQuestionSectionFilter('');
                setCopyQuestionSearchTerm('');
                setCopyQuestionPosition(null);
              }}
            >
              Cancel
            </button>
            <button
              className="modal-btn save"
              onClick={copyQuestionIntoSection}
              disabled={!copyQuestionSourceId}
            >
              📋 Copy Question
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===== MAIN RENDER =====
  // Every current section is copyable - not filtered to titles containing
  // "plans", since a newly added section (e.g. from the Medical/Basic Life
  // template cards, which don't include the word "Plans" in their default
  // title) should be available to copy immediately, regardless of what
  // it's named. This recomputes fresh from formData on every render, so
  // any section just added shows up right away.
  const copyablePlanSections = formData.sections;

  return (
    <div className="form-wrap">
      <h1>Client Implementation Clarifications</h1>
      
      <div className="toolbar">
        <button className="toolbar-btn add-section" onClick={() => setShowTemplateDialog(true)}>
          ➕ Add Section
        </button>
        <button className="toolbar-btn pdf" onClick={generatePDF}>
          📄 Generate Fillable PDF
        </button>
      </div>

      {showTemplateDialog && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>➕ Add Section</h3>
            <p>Select a section template:</p>
            <div className="template-grid">
              {SECTION_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  className="template-btn"
                  onClick={() => selectTemplateForEditing(template)}
                >
                  {template.name}
                </button>
              ))}
            </div>

            {copyablePlanSections.length > 0 && (
              <div className="editor-field" style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #e0e0e0' }}>
                <label>Or copy an existing section (structure + current answers):</label>
                <select
                  className="editor-input"
                  value={copySourceId}
                  onChange={(e) => setCopySourceId(e.target.value)}
                >
                  <option value="">Select a plan to copy</option>
                  {copyablePlanSections.map((s) => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))}
                </select>
                <button
                  className="modal-btn save"
                  style={{ marginTop: '8px', width: '100%' }}
                  onClick={copyExistingSection}
                  disabled={!copySourceId}
                >
                  📋 Copy Selected Plan
                </button>
              </div>
            )}

            <button 
              className="modal-btn cancel" 
              style={{marginTop: '15px', width: '100%'}}
              onClick={() => setShowTemplateDialog(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showOptionEditor && selectedTemplate && (
        <div className="modal-overlay">
          <div className="modal option-editor-modal" onClick={(e) => e.stopPropagation()}>
            <h3>✏️ Customize Section</h3>
            <p>Edit the section details before adding:</p>
            
            <div className="editor-field">
              <label>Section Title:</label>
              <input
                type="text"
                className="editor-input"
                value={selectedTemplate.title}
                onChange={(e) => setSelectedTemplate({
                  ...selectedTemplate,
                  title: e.target.value
                })}
              />
            </div>

            <div className="editor-field">
              <label>Insert Position:</label>
              <select 
                className="editor-input"
                value={selectedSectionIndex !== null ? selectedSectionIndex : 'end'}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'end') {
                    setSelectedSectionIndex(null);
                  } else {
                    setSelectedSectionIndex(parseInt(val));
                  }
                }}
              >
                <option value="end">At the End</option>
                {formData.sections.map((section, index) => (
                  <option key={section.id} value={index}>
                    After "{section.title}"
                  </option>
                ))}
              </select>
            </div>

            {selectedTemplate.questions.map((question, qIndex) => (
              <div key={qIndex} className="editor-question">
                <div className="editor-field">
                  <label>Question {qIndex + 1} Label:</label>
                  <input
                    type="text"
                    className="editor-input"
                    value={question.label}
                    onChange={(e) => {
                      const updated = { ...selectedTemplate };
                      updated.questions[qIndex].label = e.target.value;
                      setSelectedTemplate(updated);
                    }}
                  />
                </div>

                {question.type === 'dropdown' && (
                  <div className="editor-options">
                    <label>Dropdown Options:</label>
                    <div className="options-list">
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="option-item">
                          <input
                            type="text"
                            className="option-input"
                            value={option}
                            onChange={(e) => updateTemplateOption(qIndex, oIndex, e.target.value)}
                          />
                          <button 
                            className="option-delete-btn"
                            onClick={() => deleteTemplateOption(qIndex, oIndex)}
                            title="Delete option"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <button 
                      className="option-add-btn"
                      onClick={() => addTemplateOption(qIndex)}
                    >
                      ➕ Add Option
                    </button>
                  </div>
                )}

                {question.type === 'checkbox' && (
                  <div className="editor-options">
                    <label>Checkbox Options:</label>
                    <div className="options-list">
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="option-item">
                          <input
                            type="text"
                            className="option-input"
                            value={option}
                            onChange={(e) => {
                              const updated = { ...selectedTemplate };
                              updated.questions[qIndex].options[oIndex] = e.target.value;
                              setSelectedTemplate(updated);
                            }}
                          />
                          <button 
                            className="option-delete-btn"
                            onClick={() => {
                              if (window.confirm('Delete this option?')) {
                                const updated = { ...selectedTemplate };
                                updated.questions[qIndex].options = updated.questions[qIndex].options.filter((_, i) => i !== oIndex);
                                setSelectedTemplate(updated);
                              }
                            }}
                            title="Delete option"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <button 
                      className="option-add-btn"
                      onClick={() => {
                        const newOption = prompt('Enter new option name:');
                        if (newOption && newOption.trim()) {
                          const updated = { ...selectedTemplate };
                          updated.questions[qIndex].options = [...updated.questions[qIndex].options, newOption.trim()];
                          setSelectedTemplate(updated);
                        }
                      }}
                    >
                      ➕ Add Option
                    </button>
                  </div>
                )}
              </div>
            ))}

            <div className="modal-actions">
              <button 
                className="modal-btn cancel" 
                onClick={() => {
                  setShowOptionEditor(false);
                  setSelectedTemplate(null);
                  setSelectedSectionIndex(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="modal-btn save" 
                onClick={addSectionFromTemplate}
              >
                ➕ Add Section
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddQuestionDialog && renderAddQuestionDialog()}
      {showCopyQuestionDialog && renderCopyQuestionDialog()}

      {formData.sections.map((section, index) => (
        <div key={section.id} className="section">
          <div className="section-header">
            <div className="section-title-wrapper">
              <span className="section-number">{index + 1}.</span>
              <span 
                className="section-title-editable"
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => updateSectionTitle(section.id, e.target.textContent)}
              >
                {section.title}
              </span>
            </div>
            <button 
              className="delete-section-btn" 
              onClick={() => deleteSection(section.id)}
              title="Delete section"
            >
              🗑️ Delete Section
            </button>
          </div>
          
          {section.questions.map(question => (
            renderQuestion(question, section.id)
          ))}
          
          <div className="question-btn-row">
            <button 
              className="add-question-btn"
              onClick={() => openAddQuestionDialog(section.id)}
            >
              ➕ Add Question
            </button>
            <button 
              className="add-question-btn"
              onClick={() => openCopyQuestionDialog(section.id)}
            >
              📋 Copy Question
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default App;